import { onAnalyticsConsent, readAnalyticsConsent } from "./analytics-consent";

export const CLARITY_SCRIPT_ID = "mj-clarity";

type ClarityFn = ((...args: unknown[]) => void) & {
  q?: unknown[];
  v?: unknown;
  t?: unknown;
};

declare global {
  interface Window {
    __MJ_CLARITY_ID?: string;
    clarity?: ClarityFn;
  }
}

// The Clarity tag script at clarity.ms/tag/<id> assumes `window.clarity`
// already exists as a queue stub — the standard inline snippet defines it
// before appending the tag. We inject the tag ourselves (consent-gated), so we
// must define the same stub first; otherwise the tag script throws on its very
// first `window.clarity(...)` call and clarity.js is never loaded (no data).
function ensureClarityStub(): void {
  if (window.clarity) return;
  const stub: ClarityFn = function (...args: unknown[]) {
    (stub.q = stub.q || []).push(args);
  };
  window.clarity = stub;
}

function inject(projectId: string): void {
  if (document.getElementById(CLARITY_SCRIPT_ID)) return;
  const safeId = projectId.replace(/[^A-Za-z0-9_-]/g, "");
  if (!safeId) return;
  ensureClarityStub();
  const s = document.createElement("script");
  s.id = CLARITY_SCRIPT_ID;
  s.async = true;
  s.src = `https://www.clarity.ms/tag/${safeId}`;
  document.head.appendChild(s);
}

/**
 * Loads Microsoft Clarity only after explicit analytics consent. The server
 * exposes the project id as `window.__MJ_CLARITY_ID` without loading anything
 * (SSR HTML stays identical for every visitor, so edge caching is unaffected);
 * this runs client-side and injects the tag when consent is or becomes
 * "granted". Safe to call multiple times.
 */
export function loadClarityIfConsented(): void {
  const projectId = window.__MJ_CLARITY_ID;
  if (!projectId) return;
  if (readAnalyticsConsent() === "granted") {
    inject(projectId);
    return;
  }
  const off = onAnalyticsConsent((value) => {
    if (value !== "granted") return;
    inject(projectId);
    off();
  });
}
